<template>
	<div class="mt-6 flex flex-col gap-4 font-sans">
		<div
			class="flex flex-col items-center justify-center gap-4"
			v-if="noHistory"
		>
			<p class="text-secondary-600 m-4 text-lg">
				{{ t('newSession.noPreviousPlans') }}
			</p>
			<button
				class="text-red-500 underline"
				@click.prevent="emit('cancel')"
			>
				{{ t('general.cancel') }}
			</button>
		</div>
		<div
			v-else
			class="flex flex-col gap-4"
		>
			<div
				class="text-secondary-900 text-center text-base leading-normal font-bold"
			>
				{{ t('newSession.appointmentHistory') }}
			</div>
			<FormKit
				aria-label="plan modal name select"
				outer-class="col-span-12 max-w-full !-mb-4"
				inner-class="border-0 shadow-none !justify-center"
				selector-class="font-bold text-sm text-primary-600 $remove:justify-between justify-center bg-slate-50"
				option-class="flex justify-center text-sm text-primary-600"
				selectionWrapper-class="$remove:grow"
				type="dropdown"
				:ignore="true"
				:options="nameOptions"
				:deselect="false"
				v-model="selectedName"
				@input="handleSelectedNameChange"
			>
			</FormKit>
			<div>
				<OptionItem
					v-for="plan in visiblePlanOptions"
					:key="plan.id"
					:state="historySelectionState(plan)"
					:disabled="plan.priceBookMismatch"
					@click="!plan.priceBookMismatch && (selectedPlan = plan)"
					:show-native-title="false"
				>
					<div class="flex items-center gap-2">
						<div class="flex-1">
							<div :class="{ 'text-secondary-400': plan.priceBookMismatch }">
								{{
									funeralHomeName(plan.funeralHomeId) ??
									'[Funeral Home Not Found]'
								}}
							</div>
							<div class="text-secondary-400 text-sm">
								{{
									t('newSession.lastUpdated', {
										date: plan.formattedDate,
									})
								}}
							</div>
						</div>
						<div
							v-if="plan.priceBookMismatch"
							class="group relative z-10 flex max-h-full flex-col items-center"
						>
							<ExclamationTriangleIcon class="peer size-6 text-yellow-400" />
							<div
								class="absolute top-1/2 right-full z-10 mr-2 mb-2 w-48 -translate-y-1/2 rounded-sm bg-yellow-50 p-2 text-center text-xs text-yellow-700 opacity-0 shadow-md transition-opacity duration-200 peer-hover:opacity-100"
							>
								{{ t('newSession.priceBookMismatch') }}
							</div>
						</div>
					</div>
				</OptionItem>
			</div>

			<button
				type="submit"
				class="rounded-lg border-0 px-16 py-3 text-base font-bold"
				:class="[
					selectedPlan
						? 'bg-accent-400 text-accent-700 hover:bg-accent-700 hover:text-white'
						: 'text-secondary-400 bg-slate-200',
				]"
				@click.prevent="emit('planSelected', selectedPlan)"
			>
				{{ t('newSession.resumeAppointment') }}
			</button>
			<button
				class="text-red-500 underline"
				@click.prevent="emit('cancel')"
			>
				{{ t('general.cancel') }}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { PriceBook } from '@/classes/PriceBook'
import { SelectOptionState } from '@/enums/SelectOptionState'
import { useCrmStore } from '@/stores/crm'
import type { PlanOption } from '@/types'
import OptionItem from '@/views/NewSession/PlanVersions/OptionItem.vue'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import type { PropType } from 'vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
	plans: Array as PropType<PlanOption[]>,
	initialSelection: {
		type: Object as PropType<PlanOption> | null,
	},
	selectedPriceBook: {
		type: Object as PropType<PriceBook | undefined>,
		required: false,
	},
})

const emit = defineEmits(['planSelected', 'cancel'])

const { t } = useI18n()

const crm = useCrmStore()
const selectedPlan = ref<PlanOption | undefined>(undefined)

const selectedName = ref<string | undefined>(undefined)

const noHistory = computed(() => {
	return !props.plans?.length
})

const nameOptions = computed(() =>
	Array.from(new Set(props.plans?.map((plan) => plan.fullName)))
)

const historySelectionState = (plan: PlanOption) => {
	if (!selectedPlan.value) {
		return SelectOptionState.NoSelection
	} else if (selectedPlan.value?.id === plan.id && selectedPlan.value) {
		return SelectOptionState.Selected
	} else {
		return SelectOptionState.NotSelected
	}
}

const visiblePlanOptions = computed(() => {
	return (
		props.plans
			?.map((option: PlanOption) => ({
				...option,
				priceBookMismatch: isPriceBookMismatched(option.funeralHomeId),
			}))
			.filter((item) => item.fullName === selectedName.value) ?? []
	)
})

selectedName.value = nameOptions.value.find(
	(option) => option === props.initialSelection?.fullName
)

selectedPlan.value = props.initialSelection

const handleSelectedNameChange = () => {
	// deselect the plan option if user changes the name in dropdown
	selectedPlan.value = undefined
}

const funeralHomeName = (funeralHomeId: string) => {
	return crm.findFuneralHome(funeralHomeId)?.name
}

const isPriceBookMismatched = (funeralHomeId?: string | undefined): boolean => {
	const planPriceBook = funeralHomeId
		? crm.findPriceBookForFuneralHome(funeralHomeId)
		: undefined

	if (!planPriceBook) {
		return false
	}

	return props.selectedPriceBook?.data.id !== planPriceBook.data.id
}
</script>
